import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { logAnalyticsEvent } from '../auth/analytics';
import { qk } from './keys';
import type {
  AcceptInviteResponse,
  AccessListResponse,
  ActivitiesResponse,
  ActivityCategory,
  ActivityDetailResponse,
  ActivityResponse,
  ActivityState,
  ActivitySuggestionsResponse,
  BabiesResponse,
  BabyAccessRole,
  BabyMilestoneDetailResponse,
  BabyMilestoneRow,
  MilestoneLibraryResponse,
  MilestoneLibraryRow,
  MilestoneProgressResponse,
  BabyRow,
  CreateBabyResponse,
  EncryptionSettings,
  EncryptionSettingsResponse,
  EngagementEntityType,
  EngagementResponse,
  GearClickResponse,
  GearItemRow,
  GrowthMetricServer,
  GrowthResponse,
  InviteListResponse,
  InvitePreview,
  IssueInviteResponse,
  LeapsResponse,
  MediaListResponse,
  MediaUploadResponse,
  MeResponse,
  CancelSubscriptionResponse,
  DevSetTierResponse,
  TrialStartResponse,
  SubscriptionTier,
  MilestoneGearResponse,
  ServerMilestoneStatus,
  TaskLogRow,
  TodayResponse,
  CreateFeedbackInput,
  QuestionnairesResponse,
  SaveQuestionnaireInput,
  SaveQuestionnaireResponse,
  BabyEventsResponse,
  NotificationFrequency,
  NotificationSettingsResponse,
  ApiKeyItem,
  ApiKeyCreateResponse,
  ApiKeyListResponse,
} from './dto';
import type { GenderPreset } from '../theme';

/** Count uploaded files in a multipart body (file / file_0 / file_1 …, thumbnails excluded). */
function formFileCount(form: FormData): number {
  let n = 0;
  for (const key of form.keys()) {
    if (/^file(_\d+)?$/.test(key)) n += 1;
  }
  return n;
}

/* ── Reads ──────────────────────────────────────────────────────────── */

export function useMe(enabled = true) {
  return useQuery({
    queryKey: qk.me(),
    queryFn: () => api<MeResponse>('/me'),
    enabled,
  });
}

/** Entitlements block off /me — tier, limits, usage, trial offer. */
export function useEntitlements(enabled = true) {
  return useQuery({
    queryKey: qk.me(),
    queryFn: () => api<MeResponse>('/me'),
    enabled,
    select: (r) => r.entitlements,
  });
}

/* ── Subscription writes ────────────────────────────────────────────── */

/** Start the one-time 14-day Premium trial. */
export function useStartTrial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<TrialStartResponse>('/me/trial', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.me() });
      logAnalyticsEvent('start_trial');
    },
  });
}

/**
 * Cancel auto-renew on a monthly subscription. Access continues until the paid
 * period ends, then the user lazily falls back to free.
 */
export function useCancelSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<CancelSubscriptionResponse>('/me/subscription/cancel', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.me() });
      logAnalyticsEvent('cancel_subscription');
    },
  });
}

/**
 * DEV/TEST ONLY: grant a tier without Paddle (BILLING_MODE=mock). Used by the
 * mock checkout path in BillingProvider so the upgrade flow works offline.
 */
export function useSetDevTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { tier: SubscriptionTier; lifetime?: boolean }) =>
      api<DevSetTierResponse>('/dev/subscription', { method: 'POST', body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.me() });
      qc.invalidateQueries({ queryKey: qk.babies() });
    },
  });
}

export function useBabies(enabled = true) {
  return useQuery({
    queryKey: qk.babies(),
    queryFn: () => api<BabiesResponse>('/babies'),
    enabled,
    select: (r) => r.babies,
  });
}

export function useLeaps(enabled = true) {
  return useQuery({
    queryKey: qk.leaps(),
    queryFn: () => api<LeapsResponse>('/leaps'),
    enabled,
    // Library content rarely changes — cache it across the whole session.
    staleTime: 60 * 60_000,
    gcTime:    24 * 60 * 60_000,
    select: (r) => r.leaps,
  });
}

export function useExcludedCategories(babyId: string | null) {
  const [excluded, setExcluded] = useState<string[]>(() => {
    if (!babyId) return [];
    try {
      const stored = localStorage.getItem(`babysteps.excluded_categories_${babyId}`);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (!babyId) {
      setExcluded([]);
      return;
    }
    const handleStorage = () => {
      try {
        const stored = localStorage.getItem(`babysteps.excluded_categories_${babyId}`);
        setExcluded(stored ? JSON.parse(stored) : []);
      } catch {
        setExcluded([]);
      }
    };
    handleStorage();
    window.addEventListener('storage', handleStorage);
    window.addEventListener('babysteps:categories-changed', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('babysteps:categories-changed', handleStorage);
    };
  }, [babyId]);

  const toggleCategory = (category: string) => {
    if (!babyId) return;
    const next = excluded.includes(category)
      ? excluded.filter((c) => c !== category)
      : [...excluded, category];
    try {
      localStorage.setItem(`babysteps.excluded_categories_${babyId}`, JSON.stringify(next));
    } catch (e) {
      console.error(e);
    }
    setExcluded(next);
    window.dispatchEvent(new Event('babysteps:categories-changed'));
  };

  return [excluded, toggleCategory] as const;
}

/**
 * The static milestone catalog (names, descriptions, timing stats, editorial
 * media) — identical for every baby, so it's fetched once and cached for the
 * session instead of riding along with each per-baby load. Mirrors useLeaps.
 */
export function useMilestoneLibrary(enabled = true) {
  return useQuery({
    queryKey: qk.milestones(),
    queryFn: () => api<MilestoneLibraryResponse>('/milestones'),
    enabled,
    // Library content rarely changes — cache it across the whole session.
    staleTime: 60 * 60_000,
    gcTime:    24 * 60 * 60_000,
    select: (r) => r.milestones,
  });
}

/**
 * The same milestone catalog as useMilestoneLibrary, but via the PUBLIC,
 * no-auth endpoint (GET /catalog/milestones). Used by the pre-signup onboarding
 * preview, where there is no JWT yet. Generic data + primary editorial image
 * only — no per-baby state.
 */
export function usePublicMilestoneLibrary(enabled = true) {
  return useQuery({
    queryKey: qk.milestonesPublic(),
    queryFn: () => api<MilestoneLibraryResponse>('/catalog/milestones'),
    enabled,
    staleTime: 60 * 60_000,
    gcTime:    24 * 60 * 60_000,
    select: (r) => r.milestones,
  });
}

/** Merge one catalog row + its (optional) per-baby state into a BabyMilestoneRow. */
function mergeMilestone(lib: MilestoneLibraryRow, p?: MilestoneProgressResponse['progress'][number]): BabyMilestoneRow {
  return {
    milestone_id: lib.id,
    group_key: lib.group_key,
    leap_id: lib.leap_id,
    avg_start_weeks: lib.avg_start_weeks,
    avg_achieved_weeks: lib.avg_achieved_weeks,
    median_achieved_weeks: lib.median_achieved_weeks,
    std_dev_weeks: lib.std_dev_weeks,
    depends_on: lib.depends_on,
    sort_order: lib.sort_order,
    early_flag_weeks: lib.early_flag_weeks,
    name: p?.override_name ?? lib.name,
    description: p?.override_description ?? lib.description,
    status: p?.status ?? 'not_started',
    started_at: p?.started_at ?? null,
    achieved_date: p?.achieved_date ?? null,
    notes: p?.notes ?? null,
    updated_at: p?.updated_at ?? null,
    primary_media_id: p?.primary_media_id ?? null,
    primary_media_type: p?.primary_media_type ?? null,
    primary_media_mime: p?.primary_media_mime ?? null,
    primary_media_encrypted: p?.primary_media_encrypted ?? null,
    user_media_id: p?.user_media_id ?? null,
    user_media_type: p?.user_media_type ?? null,
    user_media_mime: p?.user_media_mime ?? null,
    user_media_encrypted: p?.user_media_encrypted ?? null,
    media: lib.media,
    has_translation: lib.has_translation,
  };
}

/**
 * Per-baby milestones, reassembled from the cached catalog + a lightweight
 * per-baby progress payload (the offline-first split). Returns the same
 * `{ milestones, counts }` shape the screens were built on, so consumers are
 * unchanged. The progress query keeps the existing `babyMilestones` key, so all
 * existing invalidations (status edits, media uploads) still refresh state.
 */
export function useBabyMilestones(babyId: string | null) {
  const [excluded] = useExcludedCategories(babyId);
  const libraryQ = useMilestoneLibrary();
  const progressQ = useQuery({
    queryKey: babyId ? qk.babyMilestones(babyId) : ['babies', 'none', 'milestones'],
    queryFn: () => api<MilestoneProgressResponse>(`/babies/${babyId}/milestones/progress`),
    enabled: !!babyId,
  });

  const data = useMemo(() => {
    if (!libraryQ.data || !progressQ.data) return undefined;
    const progress = progressQ.data.progress;
    const milestones = libraryQ.data
      .map((lib) => mergeMilestone(lib, progress[lib.id]))
      .filter((m) => !excluded.includes(m.group_key));

    return {
      milestones,
      counts: {
        achieved:    milestones.filter((m) => m.status === 'achieved').length,
        in_progress: milestones.filter((m) => m.status === 'in_progress').length,
        not_started: milestones.filter((m) => m.status === 'not_started').length,
        total:       milestones.length,
      },
    };
  }, [libraryQ.data, progressQ.data, excluded]);

  return {
    data,
    isLoading: libraryQ.isLoading || progressQ.isLoading,
    isError:   libraryQ.isError || progressQ.isError,
    error:     libraryQ.error ?? progressQ.error,
    refetch:   () => Promise.all([libraryQ.refetch(), progressQ.refetch()]),
  };
}

export function useBabyMilestone(babyId: string | null, milestoneId: number | null) {
  return useQuery({
    queryKey: babyId && milestoneId
      ? qk.babyMilestone(babyId, milestoneId)
      : ['babies', 'none', 'milestones', 0],
    queryFn: () =>
      api<BabyMilestoneDetailResponse>(`/babies/${babyId}/milestones/${milestoneId}`),
    enabled: !!babyId && !!milestoneId,
  });
}

export function useMilestoneGear(babyId: string | null, milestoneId: number | null) {
  return useQuery({
    queryKey: babyId && milestoneId
      ? qk.babyMilestoneGear(babyId, milestoneId)
      : ['babies', 'none', 'milestones', 0, 'gear'],
    queryFn: () =>
      api<MilestoneGearResponse>(`/babies/${babyId}/milestones/${milestoneId}/gear`),
    enabled: !!babyId && !!milestoneId,
    staleTime: 60 * 60_000,
    gcTime:    24 * 60 * 60_000,
    select: (r) => r.items,
  });
}

/**
 * The full recommended-gear catalog, keyed by milestone id. Baby-independent
 * (locale only), so it lives under its own query key with a long staleTime and
 * is fetched lazily — the first time a milestone/leap detail view mounts —
 * rather than being bundled into every `/babies/{id}/milestones` list load.
 */
export function useAllGear(enabled = true) {
  return useQuery({
    queryKey: qk.gear(),
    queryFn: () => api<{ gear: Record<number, GearItemRow[]> }>('/gear'),
    enabled,
    staleTime: 60 * 60_000,
    gcTime:    24 * 60 * 60_000,
    select: (r) => r.gear,
  });
}

/**
 * POST /gear/{id}/click — log an affiliate click and resolve the destination.
 * The backend picks the country-correct Amazon shop (+ affiliate tag) or, when
 * the item has no link, a country-scoped Google search. We send the device
 * country so it can resolve the right marketplace.
 */
export function useGearClick() {
  return useMutation({
    mutationFn: (vars: {
      gearItemId: number;
      country: string | null;
      platform: 'web' | 'app';
      babyId?: string;
      milestoneId?: number;
    }) =>
      api<GearClickResponse>(`/gear/${vars.gearItemId}/click`, {
        method: 'POST',
        body: {
          country: vars.country,
          platform: vars.platform,
          baby_id: vars.babyId,
          milestone_id: vars.milestoneId,
        },
      }),
    onSuccess: (_, vars) => {
      logAnalyticsEvent('gear_click', {
        gear_item_id: vars.gearItemId,
        milestone_id: vars.milestoneId,
        country: vars.country ?? undefined,
      });
    },
  });
}

/**
 * GET /babies/{id}/today — joke + cached daily task (task is null until
 * DeepSeek generation is wired up).
 *
 * Refetched a little more eagerly than the other queries — the joke can
 * change between viewings of the day.
 */
export function useToday(babyId: string | null) {
  return useQuery({
    queryKey: babyId ? qk.babyToday(babyId) : ['babies', 'none', 'today'],
    queryFn: () => api<TodayResponse>(`/babies/${babyId}/today`),
    enabled: !!babyId,
    staleTime: 60_000, // 1 minute
  });
}

/**
 * GET /babies/{id}/activity — daily counts of tasks marked done. Used by the
 * Dashboard drill-in and the Profile sheet's contribution graph.
 */
export function useActivity(babyId: string | null) {
  return useQuery({
    queryKey: babyId ? qk.babyActivity(babyId) : ['babies', 'none', 'activity'],
    queryFn: () => api<ActivityResponse>(`/babies/${babyId}/activity`),
    enabled: !!babyId,
  });
}

/**
 * GET /babies/{id}/growth — all growth records for a baby (one row per metric
 * per date). The Growth screen groups these into per-date measurements via
 * rowsToMeasurements().
 */
export function useGrowth(babyId: string | null) {
  return useQuery({
    queryKey: babyId ? qk.babyGrowth(babyId) : ['babies', 'none', 'growth'],
    queryFn: () => api<GrowthResponse>(`/babies/${babyId}/growth`),
    enabled: !!babyId,
    select: (r) => r.measurements,
  });
}

/* ── Writes ─────────────────────────────────────────────────────────── */

export interface CreateBabyInput {
  name: string;
  date_of_birth: string;     // 'YYYY-MM-DD'
  gender: GenderPreset;
  locale?: string;
}

export function useCreateBaby() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBabyInput) =>
      api<CreateBabyResponse>('/babies', { method: 'POST', body: input }),
    onSuccess: (r) => {
      qc.setQueryData<BabiesResponse>(qk.babies(), (prev) => {
        if (!prev) return { babies: [r.baby] };
        return { babies: [...prev.babies, r.baby] };
      });
      qc.setQueryData<{ baby: BabyRow }>(qk.baby(r.baby.id), { baby: r.baby });
      logAnalyticsEvent('create_baby', { baby_id: String(r.baby.id) });
    },
  });
}

export interface UpdateBabyInput {
  babyId: string;
  name?: string;
  date_of_birth?: string;     // 'YYYY-MM-DD'
  gender?: GenderPreset;
  avatar_media_id?: string | null;
  locale?: string | null;
}

export function useUpdateBaby() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, ...patch }: UpdateBabyInput) =>
      api<{ baby: BabyRow }>(`/babies/${babyId}`, { method: 'PATCH', body: patch }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babies() });
      qc.invalidateQueries({ queryKey: qk.baby(vars.babyId) });
    },
  });
}
export interface UpdateMilestoneStatusInput {
  babyId: string;
  milestoneId: number;
  status: ServerMilestoneStatus;
  achieved_date?: string | null;
  notes?: string | null;
}

export function useUpdateMilestoneStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, milestoneId, ...patch }: UpdateMilestoneStatusInput) =>
      api<{ ok: boolean }>(`/babies/${babyId}/milestones/${milestoneId}`, {
        method: 'PATCH',
        body: patch,
      }),
    onSuccess: (_, vars) => {
      // Refresh both the list (status pill in cards) and the detail (if open).
      qc.invalidateQueries({ queryKey: qk.babyMilestones(vars.babyId) });
      qc.invalidateQueries({ queryKey: qk.babyMilestone(vars.babyId, vars.milestoneId) });
      logAnalyticsEvent('update_milestone_status', {
        baby_id: vars.babyId,
        milestone_id: vars.milestoneId,
        status: vars.status,
      });
    },
  });
}

export interface MilestoneBatchUpdateItem {
  milestone_id: number;
  status: ServerMilestoneStatus;
  achieved_date?: string | null;
  notes?: string | null;
}

export interface UpdateMilestonesBatchInput {
  babyId: string;
  updates: MilestoneBatchUpdateItem[];
}

export function useUpdateMilestonesBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, updates }: UpdateMilestonesBatchInput) =>
      api<{ ok: boolean }>(`/babies/${babyId}/milestones`, {
        method: 'PATCH',
        body: { updates },
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyMilestones(vars.babyId) });
      vars.updates.forEach((update) => {
        logAnalyticsEvent('update_milestone_status', {
          baby_id: vars.babyId,
          milestone_id: update.milestone_id,
          status: update.status,
          batch: true,
        });
      });
    },
  });
}

export interface CreateGrowthInput {
  babyId: string;
  metric: GrowthMetricServer;
  value: number;
  unit: string;              // 'kg' | 'cm' (see backend chk_growth_unit_for_metric)
  recorded_at: string;       // 'YYYY-MM-DD'
  notes?: string | null;
}

/**
 * POST /babies/{id}/growth — record a single metric. The Add sheet fires one
 * of these per filled field (weight / height / head) for the chosen date.
 */
export function useCreateGrowth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, ...body }: CreateGrowthInput) =>
      api<{ id: string }>(`/babies/${babyId}/growth`, { method: 'POST', body }),
    onSuccess: (_, vars) => {
      // Prefix-invalidate so the all-metrics list and any metric-scoped
      // queries both refetch.
      qc.invalidateQueries({ queryKey: qk.babyGrowth(vars.babyId) });
      logAnalyticsEvent('add_growth_measurement', {
        baby_id: vars.babyId,
        metric: vars.metric,
      });
    },
  });
}

export function useDeleteGrowth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, growthId }: { babyId: string; growthId: string }) =>
      api<void>(`/babies/${babyId}/growth/${growthId}`, { method: 'DELETE' }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyGrowth(vars.babyId) });
    },
  });
}

/**
 * POST /babies/{id}/today/generate — ask DeepSeek for today's activity.
 * Idempotent per day; pass `force` to regenerate (the refresh button).
 */
export function useGenerateTodayTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, force }: { babyId: string; force?: boolean }) =>
      api<{ task: TaskLogRow }>(`/babies/${babyId}/today/generate`, {
        method: 'POST',
        body: force ? { force: true } : {},
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyToday(vars.babyId) });
    },
  });
}

/**
 * POST /babies/{id}/tasks/{taskId}/done — mark today's task complete. Refreshes
 * the Today card and the activity heat-map (a done task counts toward streak).
 */
export function useMarkTaskDone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, taskId }: { babyId: string; taskId: string }) =>
      api<{ ok: boolean }>(`/babies/${babyId}/tasks/${taskId}/done`, { method: 'POST' }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyToday(vars.babyId) });
      qc.invalidateQueries({ queryKey: qk.babyActivity(vars.babyId) });
      logAnalyticsEvent('complete_daily_task', { baby_id: vars.babyId });
    },
  });
}

/* ── Sharing & access ───────────────────────────────────────────────── */

/** GET /babies/{id}/access — co-parents with their roles (the member list). */
export function useBabyAccess(babyId: string | null, enabled = true) {
  return useQuery({
    queryKey: babyId ? qk.babyAccess(babyId) : ['babies', 'none', 'access'],
    queryFn: () => api<AccessListResponse>(`/babies/${babyId}/access`),
    enabled: !!babyId && enabled,
    select: (r) => r.access,
  });
}

/** GET /babies/{id}/invites — active invite links (owners/editors only). */
export function useBabyInvites(babyId: string | null, enabled = true) {
  return useQuery({
    queryKey: babyId ? qk.babyInvites(babyId) : ['babies', 'none', 'invites'],
    queryFn: () => api<InviteListResponse>(`/babies/${babyId}/invites`),
    enabled: !!babyId && enabled,
    select: (r) => r.invites,
  });
}

/** POST /babies/{id}/invites — issue a share link for a permission level. */
export function useCreateInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, role }: { babyId: string; role: Exclude<BabyAccessRole, 'owner'> }) =>
      api<IssueInviteResponse>(`/babies/${babyId}/invites`, { method: 'POST', body: { role } }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyInvites(vars.babyId) });
      logAnalyticsEvent('create_invite', { baby_id: vars.babyId, role: vars.role });
    },
  });
}

/** DELETE /babies/{id}/invites/{inviteId} — revoke a link. */
export function useRevokeInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, inviteId }: { babyId: string; inviteId: string }) =>
      api<void>(`/babies/${babyId}/invites/${inviteId}`, { method: 'DELETE' }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyInvites(vars.babyId) });
    },
  });
}

/** PATCH /babies/{id}/access/{userId} — change a co-parent's permission. */
export function useUpdateAccessRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, userId, role }: { babyId: string; userId: string; role: Exclude<BabyAccessRole, 'owner'> }) =>
      api<AccessListResponse>(`/babies/${babyId}/access/${userId}`, { method: 'PATCH', body: { role } }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyAccess(vars.babyId) });
    },
  });
}

/** DELETE /babies/{id}/access/{userId} — remove a co-parent's access. */
export function useRemoveAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, userId }: { babyId: string; userId: string }) =>
      api<void>(`/babies/${babyId}/access/${userId}`, { method: 'DELETE' }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyAccess(vars.babyId) });
    },
  });
}

/** GET /invites/{token} — public preview ("Join Lena as Editor"). */
export function useInvitePreview(token: string | null) {
  return useQuery({
    queryKey: token ? qk.invitePreview(token) : ['invites', 'none', 'preview'],
    queryFn: () => api<InvitePreview>(`/invites/${encodeURIComponent(token!)}`),
    enabled: !!token,
    staleTime: 30_000,
  });
}

/** POST /invites/{token}/accept — current user joins the shared baby. */
export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      api<AcceptInviteResponse>(`/invites/${encodeURIComponent(token)}/accept`, { method: 'POST' }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: qk.babies() });
      qc.invalidateQueries({ queryKey: qk.me() });
      logAnalyticsEvent('join_baby', {
        baby_id: String(r.baby.id),
        already_member: !!r.already_member,
      });
    },
  });
}

/* ── Profile settings ───────────────────────────────────────────────── */

/** PATCH /me — persist the user's language (and any other user-level fields). */
export function useUpdateLocale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (locale: string) =>
      api<{ ok: boolean }>('/me', { method: 'PATCH', body: { locale } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.me() });
    },
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<{ ok: boolean }>('/me/profile', { method: 'PATCH', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.me() });
    },
  });
}

/* ── Encryption settings ────────────────────────────────────────────── */

/** GET /babies/{id}/encryption — the baby's media-encryption choice (mode 'unset' until chosen). */
export function useEncryptionSettings(babyId: string | null, enabled = true) {
  return useQuery({
    queryKey: babyId ? qk.encryption(babyId) : ['babies', 'none', 'encryption'],
    queryFn: () => api<EncryptionSettingsResponse>(`/babies/${babyId}/encryption`),
    enabled: enabled && !!babyId,
    staleTime: 5 * 60_000,
    select: (r) => r.settings,
  });
}

/** PUT /babies/{id}/encryption — persist mode + (for e2ee) the non-secret KDF salt/verifier. */
export function useUpdateEncryptionSettings(babyId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<EncryptionSettings> & { mode: EncryptionSettings['mode'] }) =>
      api<EncryptionSettingsResponse>(`/babies/${babyId}/encryption`, { method: 'PUT', body }),
    onSuccess: (r, vars) => {
      if (babyId) {
        qc.setQueryData<EncryptionSettingsResponse>(qk.encryption(babyId), r);
      }
      logAnalyticsEvent('set_encryption_mode', { baby_id: babyId, mode: vars.mode });
    },
  });
}

/* ── Media ──────────────────────────────────────────────────────────── */

/** GET /babies/{id}/milestones/{mid}/media — per-baby, per-milestone gallery. */
export function useMilestoneMedia(babyId: string | null, milestoneId: number | null) {
  return useQuery({
    queryKey: babyId && milestoneId
      ? qk.babyMilestoneMedia(babyId, milestoneId)
      : ['babies', 'none', 'milestones', 0, 'media'],
    queryFn: () => api<MediaListResponse>(`/babies/${babyId}/milestones/${milestoneId}/media`),
    enabled: !!babyId && !!milestoneId,
    select: (r) => r.media,
  });
}

/**
 * GET /babies/{id}/media — most-recent image media across the whole baby.
 * Powers the Today screen's "Last 2 weeks" recap; the screen filters these by
 * created_at and takes the first few.
 */
export function useRecentMedia(babyId: string | null) {
  return useQuery({
    queryKey: babyId ? qk.babyMedia(babyId) : ['babies', 'none', 'media'],
    queryFn: () => api<MediaListResponse>(`/babies/${babyId}/media`),
    enabled: !!babyId,
    select: (r) => r.media,
  });
}

/**
 * GET /babies/{id}/media?all=1 — fetch all media (images & videos) for the baby.
 */
export function useAllBabyMedia(babyId: string | null) {
  return useQuery({
    queryKey: babyId ? [...qk.babyMedia(babyId), 'all'] : ['babies', 'none', 'media', 'all'],
    queryFn: () => api<MediaListResponse>(`/babies/${babyId}/media?all=1`),
    enabled: !!babyId,
    select: (r) => r.media,
  });
}

/**
 * GET /babies/{id}/media — but selecting only the total image count the recent
 * endpoint reports. Shares the cache entry with useRecentMedia (same key); the
 * per-observer select keeps the Dashboard's "Images" stat cheap.
 */
export function useImageCount(babyId: string | null) {
  return useQuery({
    queryKey: babyId ? qk.babyMedia(babyId) : ['babies', 'none', 'media'],
    queryFn: () => api<MediaListResponse>(`/babies/${babyId}/media`),
    enabled: !!babyId,
    select: (r) => r.count ?? r.media.length,
  });
}

/**
 * POST /babies/{id}/milestones/{mid}/media — multipart upload of the (already
 * compressed and, when E2EE, encrypted) bytes plus plaintext metadata.
 */
export function useUploadMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, milestoneId, form }: { babyId: string; milestoneId: number; form: FormData }) =>
      api<MediaUploadResponse>(`/babies/${babyId}/milestones/${milestoneId}/media`, {
        method: 'POST',
        body: form,
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyMilestoneMedia(vars.babyId, vars.milestoneId) });
      qc.invalidateQueries({ queryKey: qk.babyMilestone(vars.babyId, vars.milestoneId) });
      logAnalyticsEvent('upload_media', {
        baby_id: vars.babyId,
        target: 'milestone',
        milestone_id: vars.milestoneId,
        file_count: formFileCount(vars.form),
      });
    },
  });
}

/** DELETE /media/{id} — removes the blob + row. */
export function useDeleteMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ mediaId }: { mediaId: string; babyId: string; milestoneId: number }) =>
      api<void>(`/media/${mediaId}`, { method: 'DELETE' }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyMilestoneMedia(vars.babyId, vars.milestoneId) });
      qc.invalidateQueries({ queryKey: qk.babyMilestone(vars.babyId, vars.milestoneId) });
    },
  });
}

/* ── Activities ─────────────────────────────────────────────────────── */

/**
 * The activity catalog via the PUBLIC, no-auth endpoint (GET /catalog/activities)
 * — generic ideas + primary editorial image, no per-baby state. Used by the
 * pre-signup onboarding preview, where there is no JWT or baby yet.
 */
export function usePublicActivityCatalog(enabled = true) {
  return useQuery({
    queryKey: qk.activitiesPublic(),
    queryFn: () => api<ActivitiesResponse>('/catalog/activities'),
    enabled,
    staleTime: 60 * 60_000,
    gcTime:    24 * 60 * 60_000,
    select: (r) => r.activities,
  });
}

/** GET /babies/{id}/activities?category= — catalog + per-baby state. */
export function useActivities(babyId: string | null, category?: ActivityCategory) {
  return useQuery({
    queryKey: babyId ? qk.babyActivities(babyId, category) : ['babies', 'none', 'activities'],
    queryFn: () =>
      api<ActivitiesResponse>(
        `/babies/${babyId}/activities${category ? `?category=${category}` : ''}`,
      ),
    enabled: !!babyId,
    select: (r) => r.activities,
  });
}

/** GET /babies/{id}/activities/{aid} — full detail + done photos. */
export function useActivityDetail(babyId: string | null, activityId: number | null) {
  return useQuery({
    queryKey: babyId && activityId
      ? qk.babyActivityDetail(babyId, activityId)
      : ['babies', 'none', 'activities', 0],
    queryFn: () => api<ActivityDetailResponse>(`/babies/${babyId}/activities/${activityId}`),
    enabled: !!babyId && !!activityId,
    select: (r) => r.activity,
  });
}

/** GET /babies/{id}/activities/suggestions — the stable daily 1–3 (rule-based). */
export function useActivitySuggestions(babyId: string | null) {
  return useQuery({
    queryKey: babyId ? qk.babyActivitySuggestions(babyId) : ['babies', 'none', 'activities', 'suggestions'],
    queryFn: () => api<ActivitySuggestionsResponse>(`/babies/${babyId}/activities/suggestions`),
    enabled: !!babyId,
    staleTime: 60_000,
  });
}

/**
 * "New ideas" — fresh deterministic suggestions, optionally scoped to a
 * category. A mutation (not a query) because it advances the suggestion log.
 */
export function useRequestSuggestions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, category }: { babyId: string; category?: ActivityCategory }) => {
      const params = new URLSearchParams({ refresh: '1' });
      if (category) params.set('category', category);
      return api<ActivitySuggestionsResponse>(`/babies/${babyId}/activities/suggestions?${params}`);
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyActivitySuggestions(vars.babyId) });
      logAnalyticsEvent('request_activity_suggestions', {
        baby_id: vars.babyId,
        category: vars.category,
        ai: false,
      });
    },
  });
}

/** POST /babies/{id}/activities/suggestions/ai — DeepSeek custom suggestions. */
export function useAiSuggestions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, category }: { babyId: string; category?: ActivityCategory }) =>
      api<ActivitySuggestionsResponse>(`/babies/${babyId}/activities/suggestions/ai`, {
        method: 'POST',
        body: category ? { category } : {},
      }),
    onSuccess: (_, vars) => {
      // New AI rows appear in the catalog; refresh the list.
      qc.invalidateQueries({ queryKey: qk.babyActivities(vars.babyId) });
      logAnalyticsEvent('request_activity_suggestions', {
        baby_id: vars.babyId,
        category: vars.category,
        ai: true,
      });
    },
  });
}

/** POST /babies/{id}/activities/{aid}/state — done / not_interested / saved. */
export function useSetActivityState() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, activityId, status, is_favorite, done_date, notes }: {
      babyId: string;
      activityId: number;
      status?: ActivityState | 'not_started';
      is_favorite?: boolean;
      done_date?: string | null;
      notes?: string | null;
    }) =>
      api<{ ok: boolean }>(`/babies/${babyId}/activities/${activityId}/state`, {
        method: 'POST',
        body: { status, is_favorite, done_date, notes },
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyActivities(vars.babyId) });
      qc.invalidateQueries({ queryKey: qk.babyActivityDetail(vars.babyId, vars.activityId) });
      qc.invalidateQueries({ queryKey: qk.babyActivitySuggestions(vars.babyId) });
      logAnalyticsEvent('set_activity_state', {
        baby_id: vars.babyId,
        activity_id: vars.activityId,
        status: vars.status,
        is_favorite: vars.is_favorite,
      });
    },
  });
}

/** POST /babies/{id}/activities/{aid}/media — upload a "done" photo. */
export function useUploadActivityMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, activityId, form }: { babyId: string; activityId: number; form: FormData }) =>
      api<{ media: unknown }>(`/babies/${babyId}/activities/${activityId}/media`, {
        method: 'POST',
        body: form,
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyActivityDetail(vars.babyId, vars.activityId) });
      logAnalyticsEvent('upload_media', {
        baby_id: vars.babyId,
        target: 'activity',
        activity_id: vars.activityId,
        file_count: formFileCount(vars.form),
      });
    },
  });
}

/** POST /babies/{id}/activities/{aid}/completions — log a new completion. */
export function useAddActivityCompletion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      babyId,
      activityId,
      done_date,
      notes,
    }: {
      babyId: string;
      activityId: number;
      done_date: string;
      notes?: string | null;
    }) =>
      api<{ ok: boolean; completion: any }>(`/babies/${babyId}/activities/${activityId}/completions`, {
        method: 'POST',
        body: { done_date, notes },
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyActivities(vars.babyId) });
      qc.invalidateQueries({ queryKey: qk.babyActivityDetail(vars.babyId, vars.activityId) });
      qc.invalidateQueries({ queryKey: qk.babyActivitySuggestions(vars.babyId) });
      logAnalyticsEvent('complete_activity', {
        baby_id: vars.babyId,
        activity_id: vars.activityId,
      });
    },
  });
}

/** DELETE /babies/{id}/activities/{aid}/completions/{cid} — delete a completion. */
export function useDeleteActivityCompletion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, activityId, completionId }: { babyId: string; activityId: number; completionId: string }) =>
      api<{ ok: boolean }>(`/babies/${babyId}/activities/${activityId}/completions/${completionId}`, {
        method: 'DELETE',
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyActivities(vars.babyId) });
      qc.invalidateQueries({ queryKey: qk.babyActivityDetail(vars.babyId, vars.activityId) });
      qc.invalidateQueries({ queryKey: qk.babyActivitySuggestions(vars.babyId) });
    },
  });
}

/** POST /babies/{id}/activities/{aid}/completions/{cid}/media — upload a completion photo. */
export function useUploadCompletionMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      babyId,
      activityId,
      completionId,
      form,
    }: {
      babyId: string;
      activityId: number;
      completionId: string;
      form: FormData;
    }) =>
      api<{ media: unknown }>(`/babies/${babyId}/activities/${activityId}/completions/${completionId}/media`, {
        method: 'POST',
        body: form,
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyActivityDetail(vars.babyId, vars.activityId) });
      logAnalyticsEvent('upload_media', {
        baby_id: vars.babyId,
        target: 'activity_completion',
        activity_id: vars.activityId,
        file_count: formFileCount(vars.form),
      });
    },
  });
}

/** DELETE /media/{id} — remove one activity/completion photo (the lightbox's
 *  per-photo delete; not to be confused with deleting a whole completion). */
export function useDeleteActivityMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ mediaId }: { mediaId: string; babyId: string; activityId: number }) =>
      api<void>(`/media/${mediaId}`, { method: 'DELETE' }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyActivityDetail(vars.babyId, vars.activityId) });
    },
  });
}

/* ── Comments & reactions (engagement) ───────────────────────────────── */

/** Comments + reaction summary for one entity, scoped to a baby. */
export function useEngagement(
  babyId: string | null,
  entityType: EngagementEntityType,
  entityId: string | number | null,
) {
  const enabled = babyId !== null && entityId !== null && entityId !== '';
  return useQuery({
    queryKey: qk.engagement(babyId ?? '', entityType, entityId ?? ''),
    queryFn: () =>
      api<EngagementResponse>(
        `/babies/${babyId}/engagement?entity_type=${entityType}&entity_id=${encodeURIComponent(String(entityId))}`,
      ),
    enabled,
  });
}

interface EngagementTarget {
  babyId: string;
  entityType: EngagementEntityType;
  entityId: string | number;
}

/** POST a sanitized comment body. The server returns the refreshed payload. */
export function useAddComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, entityType, entityId, bodyHtml }: EngagementTarget & { bodyHtml: string }) =>
      api<EngagementResponse>(`/babies/${babyId}/comments`, {
        method: 'POST',
        body: { entity_type: entityType, entity_id: String(entityId), body_html: bodyHtml },
      }),
    onSuccess: (data, vars) => {
      qc.setQueryData(qk.engagement(vars.babyId, vars.entityType, vars.entityId), data);
      logAnalyticsEvent('add_comment', {
        baby_id: vars.babyId,
        entity_type: vars.entityType,
      });
    },
  });
}

export function useDeleteComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, commentId }: EngagementTarget & { commentId: string }) =>
      api<void>(`/babies/${babyId}/comments/${commentId}`, { method: 'DELETE' }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.engagement(vars.babyId, vars.entityType, vars.entityId) });
    },
  });
}

/** Toggle the current user's emoji reaction (add / switch / remove). */
export function useToggleReaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, entityType, entityId, emoji }: EngagementTarget & { emoji: string }) =>
      api<EngagementResponse>(`/babies/${babyId}/reactions`, {
        method: 'POST',
        body: { entity_type: entityType, entity_id: String(entityId), emoji },
      }),
    onSuccess: (data, vars) => {
      qc.setQueryData(qk.engagement(vars.babyId, vars.entityType, vars.entityId), data);
      logAnalyticsEvent('toggle_reaction', {
        baby_id: vars.babyId,
        entity_type: vars.entityType,
        emoji: vars.emoji,
      });
    },
  });
}

export function useCreateFeedback() {
  return useMutation({
    mutationFn: (input: CreateFeedbackInput) =>
      api<{ ok: boolean; feedback_id: string }>('/feedback', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      logAnalyticsEvent('submit_feedback');
    },
  });
}

export function useBabyPhotos(babyId: string | null) {
  return useQuery({
    queryKey: babyId ? qk.babyPhotos(babyId) : ['babies', 'none', 'photos'],
    queryFn: () => api<{ photos: any[] }>(`/babies/${babyId}/photos`),
    enabled: !!babyId,
    select: (r) => r.photos,
  });
}

export function useUploadPhotos() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, form }: { babyId: string; form: FormData }) =>
      api<{ ok: boolean; media: any[] }>(`/babies/${babyId}/photos`, {
        method: 'POST',
        body: form,
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyMedia(vars.babyId) });
      qc.invalidateQueries({ queryKey: [...qk.babyMedia(vars.babyId), 'all'] });
      qc.invalidateQueries({ queryKey: qk.babyPhotos(vars.babyId) });
      qc.invalidateQueries({ queryKey: qk.babyMilestones(vars.babyId) });
      
      const milestoneId = vars.form.get('milestone_id');
      if (milestoneId) {
        const mid = Number(milestoneId);
        if (Number.isFinite(mid)) {
          qc.invalidateQueries({ queryKey: qk.babyMilestoneMedia(vars.babyId, mid) });
          qc.invalidateQueries({ queryKey: qk.babyMilestone(vars.babyId, mid) });
        }
      }
      logAnalyticsEvent('upload_media', {
        baby_id: vars.babyId,
        target: milestoneId ? 'milestone_moment' : 'moment',
        milestone_id: milestoneId ? Number(milestoneId) : undefined,
        file_count: formFileCount(vars.form),
      });
    },
  });
}

export function useDeletePhotoEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, photoId }: { babyId: string; photoId: string }) =>
      api<void>(`/babies/${babyId}/photos/${photoId}`, {
        method: 'DELETE',
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyMedia(vars.babyId) });
      qc.invalidateQueries({ queryKey: [...qk.babyMedia(vars.babyId), 'all'] });
      qc.invalidateQueries({ queryKey: qk.babyPhotos(vars.babyId) });
    },
  });
}

/**
 * DELETE /media/{id} — remove a single photo/video from the flat baby
 * gallery or from one "moment" (journal photo entry) without deleting the
 * whole moment. Invalidates every list that could contain it.
 */
export function useDeleteBabyMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ mediaId }: { mediaId: string; babyId: string }) =>
      api<void>(`/media/${mediaId}`, { method: 'DELETE' }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyMedia(vars.babyId) });
      qc.invalidateQueries({ queryKey: [...qk.babyMedia(vars.babyId), 'all'] });
      qc.invalidateQueries({ queryKey: qk.babyPhotos(vars.babyId) });
    },
  });
}

/* ── Questionnaires ─────────────────────────────────────────────────── */

/** GET /babies/{id}/questionnaires — defs + per-baby state + question schema. */
export function useQuestionnaires(babyId: string | null) {
  return useQuery({
    queryKey: babyId ? qk.babyQuestionnaires(babyId) : ['babies', 'none', 'questionnaires'],
    queryFn: () => api<QuestionnairesResponse>(`/babies/${babyId}/questionnaires`),
    enabled: !!babyId,
    staleTime: 60_000,
  });
}

/** PUT /babies/{id}/questionnaires/{code} — save (draft or completed). */
export function useSaveQuestionnaire() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, code, answers, status = 'completed' }: SaveQuestionnaireInput) =>
      api<SaveQuestionnaireResponse>(`/babies/${babyId}/questionnaires/${code}`, {
        method: 'PUT',
        body: { answers, status },
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyQuestionnaires(vars.babyId) });
      logAnalyticsEvent('save_questionnaire', {
        baby_id: vars.babyId,
        code: vars.code,
        status: vars.status ?? 'completed',
      });
    },
  });
}

/** POST /babies/{id}/questionnaires/{code}/media — attach a photo/video. */
export function useUploadQuestionnaireMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, code, form }: { babyId: string; code: string; form: FormData }) =>
      api<{ media: unknown }>(`/babies/${babyId}/questionnaires/${code}/media`, {
        method: 'POST',
        body: form,
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyQuestionnaires(vars.babyId) });
      logAnalyticsEvent('upload_media', {
        baby_id: vars.babyId,
        target: 'questionnaire',
        questionnaire_code: vars.code,
        file_count: formFileCount(vars.form),
      });
    },
  });
}

/** DELETE /media/{id} — remove a questionnaire photo, then refresh the list. */
export function useDeleteQuestionnaireMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ mediaId }: { mediaId: string; babyId: string }) =>
      api<void>(`/media/${mediaId}`, { method: 'DELETE' }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.babyQuestionnaires(vars.babyId) });
      qc.invalidateQueries({ queryKey: qk.babyMedia(vars.babyId) });
    },
  });
}

/* ── Notification center ────────────────────────────────────────────── */

/** GET /babies/{id}/events — activity feed + unseen badge. Polls every 60s. */
export function useBabyEvents(babyId: string | null) {
  return useQuery({
    queryKey: babyId ? qk.babyEvents(babyId) : ['babies', 'none', 'events'],
    queryFn: () => api<BabyEventsResponse>(`/babies/${babyId}/events?limit=40`),
    enabled: !!babyId,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

/** POST /babies/{id}/events/seen — clear the unseen badge. */
export function useMarkEventsSeen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (babyId: string) => api<void>(`/babies/${babyId}/events/seen`, { method: 'POST' }),
    onSuccess: (_, babyId) => {
      qc.invalidateQueries({ queryKey: qk.babyEvents(babyId) });
    },
  });
}

/** GET /me/notification-settings — per-baby enabled + frequency. */
export function useNotificationSettings(enabled = true) {
  return useQuery({
    queryKey: qk.notificationSettings(),
    queryFn: () => api<NotificationSettingsResponse>('/me/notification-settings'),
    enabled,
    staleTime: 60_000,
  });
}

/** PUT /babies/{id}/notification-settings */
export function useUpdateNotificationSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ babyId, ...body }: { babyId: string; enabled?: boolean; frequency?: NotificationFrequency }) =>
      api<{ baby_id: string; enabled: boolean; frequency: NotificationFrequency }>(
        `/babies/${babyId}/notification-settings`,
        { method: 'PUT', body },
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: qk.notificationSettings() });
      logAnalyticsEvent('update_notification_settings', {
        baby_id: vars.babyId,
        enabled: vars.enabled,
        frequency: vars.frequency,
      });
    },
  });
}

/* ── API Keys ────────────────────────────────────────────────────────── */

/** GET /me/api-keys — list personal API keys for the current user. */
export function useApiKeys(enabled = true) {
  return useQuery({
    queryKey: qk.apiKeys(),
    queryFn: () => api<ApiKeyListResponse>('/me/api-keys'),
    enabled,
    staleTime: 30_000,
    select: (r) => r.api_keys,
  });
}

/** POST /me/api-keys — mint a new personal API key. */
export function useCreateApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; expires_at?: string | null }) =>
      api<ApiKeyCreateResponse>('/me/api-keys', { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.apiKeys() });
    },
  });
}

/** DELETE /me/api-keys/{id} — revoke an API key. */
export function useDeleteApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) => api<void>(`/me/api-keys/${keyId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.apiKeys() });
    },
  });
}

